import { useCallback, SetStateAction } from 'react';
import { Dayjs } from 'dayjs';
import { IncomingCallsRequest } from '../../../../store/reducers/IncomingCallsSlice';
import { ReportRequest } from '../../../../store/reducers/ReportSlice';
import { useIncomingHandlersFilter } from './useIncomingHandlersFilter/useIncomingHandlersFilter';
import { RangeValue } from './types';
import { formatDate } from '../../../../utils';

interface Props {
  onIndex: (value: SetStateAction<number | undefined>) => void;
  onCallRequest: (value: IncomingCallsRequest) => void;
  callRequest: IncomingCallsRequest;
  onCallRequestReport: (value: ReportRequest) => void;
  callRequestReport: ReportRequest;
  onPageSize: (value: SetStateAction<number>) => void;
  total: number;
}

export const useIncomingCallsHandlers = ({
  onIndex,
  onCallRequest,
  callRequest,
  onCallRequestReport,
  callRequestReport,
  onPageSize,
  total,
}: Props) => {
  const {
    handleClinicsChange,
    clinicValue,
    handleOpenClinics,
    handleTaskStatusChange,
    taskStatusValue,
    handleOpenTaskStatus,
    handleAttemptStatusChange,
    attemptStatusValue,
    handleOpenAttemptStatus,
    handleResultChange,
    resultValue,
    handleOpenResult,
    handleScenarioChange,
    scenarioValue,
    handleOpenScenario,
    resetFilters,
    isResetDisabled,
  } = useIncomingHandlersFilter({
    onCallRequest,
    callRequest,
    onCallRequestReport,
    callRequestReport,
  });

  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onCallRequest({
        ...callRequest,
        'Filter.TextSearch': e.target.value,
      });

      onCallRequestReport({
        ...callRequestReport,
        TextSearch: e.target.value,
      });
    },
    [onCallRequest, callRequest, onCallRequestReport, callRequestReport],
  );

  const handleRangePicker = useCallback(
    (values: RangeValue<Dayjs>, formatString: string[]) => {
      formatString &&
        onCallRequest({
          ...callRequest,
          'Filter.From': formatDate(formatString[0]),
          'Filter.To': formatDate(formatString[1]),
        });

      onCallRequestReport({
        ...callRequestReport,
        From: formatDate(formatString[0]),
        To: formatDate(formatString[1]),
      });
    },
    [onCallRequest, callRequest, onCallRequestReport, callRequestReport],
  );

  const handleChange = useCallback(
    (page: number, pageItemCount: number) => {
      onPageSize(pageItemCount);
      onCallRequest({
        ...callRequest,
        PageIndex: page - 1,
        PageSize: pageItemCount,
      });
      onIndex(undefined);
    },
    [onCallRequest, callRequest, onPageSize, onIndex],
  );

  const handlePageSizeChange = useCallback(
    (value: string) => {
      const page =
        callRequest.PageIndex <= Math.floor(total / Number(value))
          ? callRequest.PageIndex
          : Math.floor(total / Number(value));
      onPageSize(Number(value));
      onCallRequest({
        ...callRequest,
        PageIndex: page,
        PageSize: Number(value),
      });
      onIndex(undefined);
    },
    [callRequest, total, onPageSize, onCallRequest, onIndex],
  );

  return {
    handleSearch,
    handleRangePicker,
    handleChange,
    handlePageSizeChange,
    handleClinicsChange,
    clinicValue,
    handleOpenClinics,
    handleTaskStatusChange,
    taskStatusValue,
    handleOpenTaskStatus,
    handleAttemptStatusChange,
    attemptStatusValue,
    handleOpenAttemptStatus,
    handleResultChange,
    resultValue,
    handleOpenResult,
    handleScenarioChange,
    scenarioValue,
    handleOpenScenario,
    resetFilters,
    isResetDisabled,
  };
};
